import {
  AfterViewInit,
  ComponentRef,
  Optional,
  ViewContainerRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  PLATFORM_ID,
  Output,
  Renderer2
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {NavigationStart, Router} from '@angular/router';
import {PullToRefreshSession} from './pull-to-refresh-session';
import {PullToRefreshIndicatorComponent} from './pull-to-refresh-indicator.component';

@Directive({
  selector: '[appPullToRefresh]',
  exportAs: 'appPullToRefresh'
})
export class PullToRefreshDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input() pullToRefreshContext: unknown;
  @Input() pullToRefreshDisabled = false;
  @Input() pullToRefreshThreshold = 120;
  @Input() pullToRefreshMax = 32;
  @Input() pullToRefreshEnableWheel = false;
  @Input() pullToRefreshScrollTarget: HTMLElement | (() => HTMLElement | null) | null = null;
  @Input() pullToRefreshIndicatorTarget: HTMLElement | null = null;
  @Output() refresh = new EventEmitter<PullToRefreshSession>();

  private static readonly claimedEvents = new WeakSet<Event>();
  private static readonly indicatorOwners = new WeakMap<HTMLElement, PullToRefreshDirective>();
  private indicatorHost: HTMLElement | null = null;
  private session?: PullToRefreshSession;
  private indicator?: ComponentRef<PullToRefreshIndicatorComponent>;
  private startX = 0;
  private startY = 0;
  private verticalGesture = false;
  private readonly directionThreshold = 12;
  private pullDistance = 0;
  private pulling = false;
  private refreshing = false;
  private spacer: HTMLElement | null = null;
  private scrollElement: HTMLElement | null = null;
  private listeners: Array<() => void> = [];
  private activePointerId: number | null = null;
  private blockPointerClick = false;
  private wheelDistance = 0;
  private wheelResetTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isBrowser: boolean;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
    @Optional() private readonly viewContainer?: ViewContainerRef,
    @Optional() private readonly router?: Router
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pullToRefreshContext'] && !changes['pullToRefreshContext'].firstChange) {
      this.cancelPull();
      this.session?.cancel(false);
    }
    if (changes['pullToRefreshIndicatorTarget'] && this.spacer) {
      this.cancelPull();
      this.session?.cancel(false);
      this.resetWheelState();
      this.moveIndicator();
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setup();
    const navigation = this.router?.events.subscribe(event => {
      if (event instanceof NavigationStart) this.session?.cancel(false);
    });
    if (navigation) this.listeners.push(() => navigation.unsubscribe());
  }

  ngOnDestroy(): void {
    this.cancelPull();
    this.session?.cancel(false);
    this.resetWheelState();
    this.indicator?.destroy();
    this.indicator = undefined;
    this.listeners.forEach((remove) => remove());
    this.listeners = [];
    if (this.spacer?.parentNode) {
      this.renderer.removeChild(this.spacer.parentNode, this.spacer);
    }
    this.spacer = null;
    this.indicatorHost = null;
  }

  complete(): void {
    this.session?.complete();
  }

  private reset(): void {
    this.refreshing = false;
    this.releasePointer();
    this.pulling = false;
    this.pullDistance = 0;
    this.updateIndicator(0, false);
  }

  private setup(): void {
    if (!this.isBrowser) {
      return;
    }

    const host = this.el.nativeElement;
    this.renderer.addClass(host, 'ptr-container');
    const style = getComputedStyle(host);
    if (style.position === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }

    this.scrollElement = this.getScrollElement(host);
    this.spacer = this.createSpacer();
    this.moveIndicator();

    this.zone.runOutsideAngular(() => {
      const onClick = (event: MouseEvent) => {
        const blocked = this.blockPointerClick;
        this.blockPointerClick = false;
        if (blocked && !this.ignoresGesture(event.target as HTMLElement | null)) {
          event.preventDefault();
          event.stopPropagation();
        }
      };
      host.addEventListener('click', onClick, true);
      this.listeners.push(() => host.removeEventListener('click', onClick, true));
      if (this.pullToRefreshEnableWheel) {
        const onWheel = (event: WheelEvent) => this.handleWheel(event);
        host.addEventListener('wheel', onWheel, { passive: false });
        this.listeners.push(() => host.removeEventListener('wheel', onWheel));
      }

      // Register both paths. Touch capability can change without recreating the
      // directive (DevTools emulation), and hybrid devices also use a mouse.
      const onTouchStart = (event: TouchEvent) => this.handleTouchStart(event);
      const onTouchMove = (event: TouchEvent) => this.handleTouchMove(event);
      const onTouchEnd = () => this.handleTouchEnd();
      const onTouchCancel = () => this.cancelPull();
      host.addEventListener('touchstart', onTouchStart, {passive: true});
      host.addEventListener('touchmove', onTouchMove, {passive: false});
      host.addEventListener('touchend', onTouchEnd, {passive: true});
      host.addEventListener('touchcancel', onTouchCancel, {passive: true});
      this.listeners.push(() => host.removeEventListener('touchstart', onTouchStart));
      this.listeners.push(() => host.removeEventListener('touchmove', onTouchMove));
      this.listeners.push(() => host.removeEventListener('touchend', onTouchEnd));
      this.listeners.push(() => host.removeEventListener('touchcancel', onTouchCancel));

      if (typeof PointerEvent !== 'undefined') {
        const onPointerDown = (event: PointerEvent) => { if (event.pointerType !== 'touch') this.handlePointerStart(event); };
        const onPointerMove = (event: PointerEvent) => { if (event.pointerType !== 'touch') this.handlePointerMove(event); };
        const onPointerEnd = (event: PointerEvent) => {
          if (event.pointerType === 'touch' || this.activePointerId === null || event.pointerId !== this.activePointerId) return;
          this.handlePointerEnd();
          this.releasePointer();
        };
        const onPointerCancel = (event: PointerEvent) => {
          if (event.pointerId === this.activePointerId) this.cancelPull();
        };
        host.addEventListener('pointerdown', onPointerDown, {passive: true});
        host.addEventListener('pointermove', onPointerMove, {passive: false});
        host.addEventListener('lostpointercapture', onPointerCancel);
        // A mouse can be released outside the host before it acquires capture.
        const document = host.ownerDocument;
        document.addEventListener('pointerup', onPointerEnd, {passive: true});
        document.addEventListener('pointercancel', onPointerCancel, {passive: true});
        this.listeners.push(() => host.removeEventListener('pointerdown', onPointerDown));
        this.listeners.push(() => host.removeEventListener('pointermove', onPointerMove));
        this.listeners.push(() => host.removeEventListener('lostpointercapture', onPointerCancel));
        this.listeners.push(() => document.removeEventListener('pointerup', onPointerEnd));
        this.listeners.push(() => document.removeEventListener('pointercancel', onPointerCancel));
      }
    });
  }

  private handleTouchStart(event: TouchEvent): void {
    this.cancelPull();
    if (PullToRefreshDirective.claimedEvents.has(event)) return;
    if (this.refreshing) {
      PullToRefreshDirective.claimedEvents.add(event);
      return;
    }
    if (this.pullToRefreshDisabled || event.touches.length !== 1) {
      return;
    }
    const touchTarget = event.target as HTMLElement | null;
    if (this.ignoresGesture(touchTarget)) return;
    this.scrollElement = this.getScrollElement(this.el.nativeElement, touchTarget);
    if (this.getScrollTop() > 0) {
      return;
    }
    if (!this.claimIndicator()) return;
    PullToRefreshDirective.claimedEvents.add(event);
    this.pulling = true;
    this.verticalGesture = false;
    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.pullDistance = 0;
  }

  private handleTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1 || this.pullToRefreshDisabled) {
      this.cancelPull();
      return;
    }
    if (!this.pulling || this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    if (this.getScrollTop() > 0) {
      this.cancelPull();
      return;
    }
    this.updatePull(event.touches[0].clientX, event.touches[0].clientY, event);
  }

  private handleTouchEnd(): void {
    if (!this.pulling) {
      return;
    }
    if (this.pullToRefreshDisabled || this.refreshing || this.getScrollTop() > 0) {
      this.cancelPull();
      return;
    }
    this.pulling = false;
    if (this.pullDistance >= this.pullToRefreshThreshold) {
      this.beginRefresh();
      return;
    }
    this.updateIndicator(0, false);
  }

  private handlePointerStart(event: PointerEvent): void {
    this.blockPointerClick = false;
    this.cancelPull();
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }
    if (PullToRefreshDirective.claimedEvents.has(event)) return;
    if (this.refreshing) {
      PullToRefreshDirective.claimedEvents.add(event);
      return;
    }
    if (this.pullToRefreshDisabled) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (this.ignoresGesture(target)) return;
    this.scrollElement = this.getScrollElement(this.el.nativeElement, target);
    if (this.getScrollTop() > 0) {
      return;
    }
    if (!this.claimIndicator()) return;
    PullToRefreshDirective.claimedEvents.add(event);
    this.pulling = true;
    this.activePointerId = event.pointerId ?? null;
    this.verticalGesture = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.pullDistance = 0;
  }

  private handlePointerMove(event: PointerEvent): void {
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
    if ((event.pointerType === 'mouse' && event.buttons !== 1) || this.pullToRefreshDisabled) {
      this.cancelPull();
      return;
    }
    if (!this.pulling || this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    if (this.getScrollTop() > 0) {
      this.cancelPull();
      return;
    }
    this.updatePull(event.clientX, event.clientY, event);
  }

  private updatePull(clientX: number, clientY: number, event: Event): void {
    const distance = clientY - this.startY;
    if (!this.verticalGesture) {
      const horizontalDistance = Math.abs(clientX - this.startX);
      if (Math.max(horizontalDistance, Math.abs(distance)) < this.directionThreshold) {
        return;
      }
      // Decide once: scrolling up or swiping sideways cannot become a refresh
      // when the user reverses direction or reaches the top later in this gesture.
      if (horizontalDistance > Math.abs(distance) || distance <= 0) {
        this.cancelPull();
        return;
      }
      this.verticalGesture = true;
      this.blockPointerClick = this.activePointerId !== null;
      if (this.activePointerId !== null) {
        try { this.el.nativeElement.setPointerCapture(this.activePointerId); } catch { /* The pointer may already have ended. */ }
      }
    }
    if (distance <= 0) {
      this.pullDistance = 0;
      this.updateIndicator(0, false);
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.pullDistance = distance;
    this.updateIndicator(Math.min(distance, this.pullToRefreshMax), distance >= this.pullToRefreshThreshold);
  }

  private handlePointerEnd(): void {
    this.handleTouchEnd();
  }

  private releasePointer(): void {
    const pointerId = this.activePointerId;
    this.activePointerId = null;
    if (pointerId === null) return;
    try {
      if (this.el.nativeElement.hasPointerCapture(pointerId)) this.el.nativeElement.releasePointerCapture(pointerId);
    } catch { /* Capture may have been released by the browser. */ }
  }

  private cancelPull(): void {
    this.blockPointerClick = false;
    this.releasePointer();
    if (!this.pulling) {
      return;
    }
    this.pulling = false;
    this.verticalGesture = false;
    this.pullDistance = 0;
    this.updateIndicator(0, false);
  }

  private moveIndicator(): void {
    this.indicatorHost = this.pullToRefreshIndicatorTarget || this.el.nativeElement;
    this.renderer.insertBefore(this.indicatorHost, this.spacer, this.indicatorHost.firstChild);
  }

  private claimIndicator(): boolean {
    const host = this.indicatorHost || this.el.nativeElement;
    const owner = PullToRefreshDirective.indicatorOwners.get(host);
    if (owner && owner !== this) return false;
    PullToRefreshDirective.indicatorOwners.set(host, this);
    return true;
  }

  private ignoresGesture(target: HTMLElement | null): boolean {
    return !!target?.closest('[data-pull-to-refresh-ignore], button, a, input, textarea, select, [contenteditable="true"], video, audio');
  }

  private beginRefresh(): void {
    this.refreshing = true;
    const session = new PullToRefreshSession(() => {
      if (this.session === session) this.zone.run(() => this.reset());
    });
    this.session = session;
    this.updateIndicator(32, true, true);
    this.zone.run(() => this.refresh.emit(session));
  }

  private handleWheel(event: WheelEvent): void {
    if (PullToRefreshDirective.claimedEvents.has(event)) return;
    if (this.ignoresGesture(event.target as HTMLElement | null)) return;
    if (this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    this.scrollElement = this.getScrollElement(this.el.nativeElement, event.target as HTMLElement | null);
    if (!this.claimIndicator()) return;
    if (this.getScrollTop() > 0) {
      this.resetWheelState();
      return;
    }
    if (event.deltaY >= 0) {
      this.resetWheelState();
      return;
    }

    PullToRefreshDirective.claimedEvents.add(event);
    this.wheelDistance += Math.abs(event.deltaY);
    this.updateIndicator(Math.min(this.wheelDistance, this.pullToRefreshMax), this.wheelDistance >= this.pullToRefreshThreshold);
    if (event.cancelable) {
      event.preventDefault();
    }

    if (this.wheelDistance >= this.pullToRefreshThreshold) {
      this.beginRefresh();
      this.resetWheelState();
      return;
    }

    if (this.wheelResetTimer) {
      clearTimeout(this.wheelResetTimer);
    }
    this.wheelResetTimer = setTimeout(() => this.resetWheelState(), 300);
  }

  private resetWheelState(): void {
    this.wheelDistance = 0;
    if (this.wheelResetTimer) {
      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = null;
    }
    if (!this.refreshing) {
      this.updateIndicator(0, false);
    }
  }

  private updateIndicator(distance: number, ready: boolean, refreshing = false): void {
    if (!this.spacer) {
      return;
    }
    this.indicator?.setInput('refreshing', refreshing);
    this.spacer.style.height = `${distance}px`;
    this.spacer.classList.toggle('ptr-ready', ready);
    this.spacer.classList.toggle('ptr-refreshing', refreshing);
    this.el.nativeElement.setAttribute('aria-busy', String(refreshing));
    if (this.indicatorHost && this.indicatorHost !== this.el.nativeElement &&
        (!PullToRefreshDirective.indicatorOwners.has(this.indicatorHost) ||
          PullToRefreshDirective.indicatorOwners.get(this.indicatorHost) === this)) {
      this.indicatorHost.setAttribute('aria-busy', String(refreshing));
    }
    if (!distance && !refreshing && !this.pulling) {
      const host = this.indicatorHost || this.el.nativeElement;
      if (PullToRefreshDirective.indicatorOwners.get(host) === this) {
        PullToRefreshDirective.indicatorOwners.delete(host);
      }
    }
  }

  private createSpacer(): HTMLElement {
    const spacer = this.renderer.createElement('div');
    this.renderer.addClass(spacer, 'ptr-spacer');
    if (this.viewContainer) {
      this.indicator = this.viewContainer.createComponent(PullToRefreshIndicatorComponent);
      this.indicator.instance.cancel.subscribe(() => this.session?.cancel());
      this.renderer.appendChild(spacer, this.indicator.location.nativeElement);
    }
    return spacer;
  }

  private getScrollElement(host: HTMLElement, target?: HTMLElement | null): HTMLElement {
    if (!this.isBrowser) {
      return host;
    }

    const explicit = typeof this.pullToRefreshScrollTarget === 'function'
      ? this.pullToRefreshScrollTarget() : this.pullToRefreshScrollTarget;
    if (explicit) return explicit;
    if (target) {
      const scrollable = this.findScrollableAncestor(target, host);
      if (scrollable) {
        return scrollable;
      }
    }
    // An empty or short overflow container still owns its gestures. Falling
    // back to the document here makes refresh depend on the number of rows
    // and on an unrelated page scroll position.
    if (this.isScrollContainer(host) || host.scrollHeight > host.clientHeight) {
      return host;
    }
    return (document.scrollingElement || document.documentElement) as HTMLElement;
  }

  private getScrollTop(): number {
    if (!this.isBrowser) {
      return this.scrollElement?.scrollTop || 0;
    }

    const documentScroller = document.scrollingElement || document.documentElement;
    if (this.scrollElement && this.scrollElement !== documentScroller &&
        this.scrollElement !== document.documentElement && this.scrollElement !== document.body) {
      return Math.max(0, this.scrollElement.scrollTop);
    }

    return Math.max(
      0,
      documentScroller.scrollTop || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0,
      window.scrollY || 0
    );
  }

  private findScrollableAncestor(target: HTMLElement, host: HTMLElement): HTMLElement | null {
    if (!this.isBrowser) {
      return null;
    }

    let current: HTMLElement | null = target;
    while (current && current !== host) {
      if (this.isScrollContainer(current) && current.scrollHeight > current.clientHeight) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private isScrollContainer(element: HTMLElement): boolean {
    const overflowY = getComputedStyle(element).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  }
}
